import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import BusinessIcon from '@mui/icons-material/Business';
import LanguageIcon from '@mui/icons-material/Language';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ImageIcon from '@mui/icons-material/Image';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import { CatagoryTypes } from '../../../../constants.js';
import SearchableDropdown from '../../../../components/FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import useOnboardingSx from '../../hooks/useOnboardingSx.js';
import StepPanel from '../../components/StepPanel.jsx';
import WizardFooter from '../../components/WizardFooter.jsx';
import Field from '../../components/ui/Field.jsx';
import FormSection from '../../components/ui/FormSection.jsx';
import RequiredLabel from '../../components/ui/RequiredLabel.jsx';
import LogoUpload from './LogoUpload.jsx';
import SubdomainPreview from './SubdomainPreview.jsx';
import useOrganisationStep from './useOrganisationStep.js';
import {
  SESSION_TIMEOUT_DAYS,
  SESSION_TIMEOUT_HOURS,
  SESSION_TIMEOUT_MINUTES,
  sanitizeMobileInput,
  sanitizeSubdomainInput,
} from './organisationConstants.js';

export default function Details() {
  const sx = useOnboardingSx();
  const {
    organisation: o,
    steps,
    stepErrors,
    currentStep,
    businessTypes,
    businessTypesLoading,
    licenses,
    licensesLoading,
    isUsingFallback,
    subTypeOptions,
    previewUrl,
    displayPostfix,
    updateOrg,
    onSelectLicense,
    onSelectBusinessType,
    onSelectBusinessSubType,
    handleLogo,
  } = useOrganisationStep();

  return (
    <>
      <StepPanel stepId={1} stepStatus={steps.organisation}>
        <Stack spacing={3.5} divider={<Divider flexItem />}>
          <FormSection
            icon={<BusinessIcon fontSize="small" />}
            title="Basic information"
            description="Primary contact and organisation identity"
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Field
                  label="Organisation name"
                  required
                  placeholder="e.g. Acme Corporation"
                  value={o.name}
                  onChange={(e) => updateOrg('name', e.target.value)}
                  error={!!stepErrors.name}
                  helperText={stepErrors.name}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  label="Mobile number"
                  required
                  placeholder="10-digit mobile"
                  value={o.mobile}
                  onChange={(e) => updateOrg('mobile', sanitizeMobileInput(e.target.value))}
                  error={!!stepErrors.mobile}
                  helperText={stepErrors.mobile}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  label="Email"
                  required
                  type="email"
                  placeholder="admin@company.com"
                  value={o.email}
                  onChange={(e) => updateOrg('email', e.target.value)}
                  error={!!stepErrors.email}
                  helperText={stepErrors.email}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  select
                  label="Category"
                  required
                  value={o.category}
                  onChange={(e) => updateOrg('category', e.target.value)}
                  error={!!stepErrors.category}
                  helperText={stepErrors.category || 'Organisation category in the platform'}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    Select category
                  </MenuItem>
                  {CatagoryTypes.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            icon={<CardMembershipIcon fontSize="small" />}
            title="License plan"
            description="Select the license plan assigned to this organisation"
          >
            {isUsingFallback && (
              <Alert severity="warning" sx={sx.alert}>
                License API is unavailable. Showing sample license data.
              </Alert>
            )}
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Field
                  select
                  label="License plan"
                  required
                  value={o.license_id || ''}
                  onChange={(e) => onSelectLicense(e.target.value)}
                  error={!!stepErrors.license_id}
                  helperText={stepErrors.license_id || 'Plan shown to the organisation (alias)'}
                  InputLabelProps={{ shrink: true }}
                  disabled={licensesLoading}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="" disabled>
                    {licensesLoading ? 'Loading licenses…' : 'Select license plan'}
                  </MenuItem>
                  {licenses.map((license) => (
                    <MenuItem key={license.id} value={String(license.id)}>
                      {license.alias || license.name}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            icon={<BusinessIcon fontSize="small" />}
            title="Business classification"
            description="Type and sub-type drive templates and defaults across the platform"
          >
            {businessTypesLoading && businessTypes.length === 0 ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading business types…
                </Typography>
              </Stack>
            ) : null}
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <SearchableDropdown
                  label={<RequiredLabel>Business type</RequiredLabel>}
                  options={businessTypes}
                  value={o.selectedBusinessTypes?.length ? o.selectedBusinessTypes : null}
                  multiSelect
                  labelKey="business_type"
                  valueKey="id"
                  onChange={onSelectBusinessType}
                  loading={businessTypesLoading}
                  disabled={businessTypesLoading && businessTypes.length === 0}
                  error={!!stepErrors.business_type}
                  errorMessage={stepErrors.business_type}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SearchableDropdown
                  label={<RequiredLabel>Business sub-type</RequiredLabel>}
                  options={subTypeOptions}
                  value={o.selectedBusinessSubTypes?.length ? o.selectedBusinessSubTypes : null}
                  multiSelect
                  labelKey="business_sub_type"
                  valueKey="id"
                  onChange={onSelectBusinessSubType}
                  disabled={!o.selectedBusinessTypes?.length}
                  error={!!stepErrors.business_subtype}
                  errorMessage={
                    stepErrors.business_subtype ||
                    (!o.selectedBusinessTypes?.length ? 'Select business type first' : '')
                  }
                  size="small"
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            icon={<LanguageIcon fontSize="small" />}
            title="Workspace URL"
            description="Unique subdomain for your organisation portal"
          >
            <Field
              label="Subdomain"
              placeholder="mycompany"
              value={o.subdomain}
              onChange={(e) => updateOrg('subdomain', sanitizeSubdomainInput(e.target.value))}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      https://
                    </Typography>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}
                    >
                      {displayPostfix}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
            <SubdomainPreview url={previewUrl} />
          </FormSection>

          <FormSection
            icon={<ScheduleIcon fontSize="small" />}
            title="Session & notes"
            description="Default idle timeout and internal comments"
          >
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="caption" sx={sx.sectionSubheading}>
                  Default session timeout
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Field
                      select
                      fullWidth
                      label="Days"
                      value={o.sessionTimeoutDays}
                      onChange={(e) => updateOrg('sessionTimeoutDays', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    >
                      {SESSION_TIMEOUT_DAYS.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={4}>
                    <Field
                      select
                      fullWidth
                      label="Hours"
                      value={o.sessionTimeoutHours}
                      onChange={(e) => updateOrg('sessionTimeoutHours', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    >
                      {SESSION_TIMEOUT_HOURS.map((h) => (
                        <MenuItem key={h} value={h}>
                          {h}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={4}>
                    <Field
                      select
                      fullWidth
                      label="Minutes"
                      value={o.sessionTimeoutMinutes}
                      onChange={(e) => updateOrg('sessionTimeoutMinutes', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    >
                      {SESSION_TIMEOUT_MINUTES.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Field
                  label="Comments"
                  multiline
                  minRows={3}
                  placeholder="Internal notes for your team (optional)"
                  value={o.comments}
                  onChange={(e) => updateOrg('comments', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection
            icon={<ImageIcon fontSize="small" />}
            title="Branding"
            description="Logo shown in emails and your organisation profile"
          >
            <LogoUpload fileName={o.logoFileName} onSelectFile={handleLogo} />
          </FormSection>
        </Stack>
      </StepPanel>
      <WizardFooter stepId={currentStep} />
    </>
  );
}
